import {zip_short} from '../array';

/** Google Home App help center article. */
export const LEARN_MORE_GOOGLE_HOME_URL =
  'https://support.google.com/chromecast/answer/7071794';

/** Play Store for the Google Home app. */
export const GOOGLE_HOME_PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.google.android.apps.chromecast.app&hl=en';

/** App Store for the Google Home app. */
export const GOOGLE_HOME_APP_STORE_URL =
  'https://apps.apple.com/us/app/google-home/id680819774';

/** Restart Router specifications URL. */
export const RESTART_ROUTER_SPECIFICATIONS_URL =
  'https://support.google.com/fiber/answer/2446100#zippy=%2Cwhat-you-need-to-get-connected';

/** Max length of device name. */
export const DEVICE_NAME_MAX_LENGTH = 63;

/** RegEx for device name validation. */
export const DEVICE_NAME_REGEXP: RegExp = /^[A-Z0-9][A-Z0-9- ]{0,62}$/i;

/** SSID Password RegExp pattern. */
export const SSID_PASSWORD_REGEXP: RegExp =
  /^[A-Z0-9`~!@#$%^&*()_=+/\\[\]{}|;:'",<.>? -]+$/i;

/** SSID Name RegExp pattern. */
export const SSID_NAME_REGEXP: RegExp =
  /^[A-Z0-9`~!@#$%^&*()_=+/\\[\]{}|;:'",<.>? -]+$/i;

/** Max length of SSID name. */
export const SSID_NAME_MAX_LENGTH = 32;

/** Min length of SSID password. */
export const SSID_PASSWORD_MIN_LENGTH = 8;

/** Max length of SSID password. */
export const SSID_PASSWORD_MAX_LENGTH = 63;

/** Frontend validation max length of a BYOR field (make and model). */
export const BYOR_FIELD_MAX_LENGTH = 128;

/** IPv4 pattern. Captures the individual octets. */
export const IPV4_REGEXP: RegExp = new RegExp(
  '^' +
    '([1-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])\\.' + // [1-255].
    '([0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])\\.' + // [0-255].
    '([0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])\\.' + // [0-255].
    '([0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])' + // [0-255]
    '$',
);

/** Port max range. */
export const PORT_MAX_RANGE = 65536;

/** Port default range start. */
export const DEFAULT_RANGE_START = 8080;

/** Port default range end. */
export const DEFAULT_RANGE_END = 8081;

export interface InputErrorProps {
  error?: boolean;
  errorMessage?: string;
  isDirty?: boolean;
  isValid?: boolean;
}

export const SUBNET_MASKS: ReadonlySet<string> = new Set([
  '255.0.0.0',
  '255.128.0.0',
  '255.192.0.0',
  '255.224.0.0',
  '255.240.0.0',
  '255.248.0.0',
  '255.252.0.0',
  '255.254.0.0',
  '255.255.0.0',
  '255.255.128.0',
  '255.255.192.0',
  '255.255.224.0',
  '255.255.240.0',
  '255.255.248.0',
  '255.255.252.0',
  '255.255.254.0',
  '255.255.255.0',
  '255.255.255.128',
  '255.255.255.192',
  '255.255.255.224',
  '255.255.255.240',
  '255.255.255.248',
  '255.255.255.252',
]);
/**
 * Parses a string spec IPv4 address into an array of its numerical
 * representation.
 *
 * @param addr The string spec containing a well-formatted IPv4 address.
 */
export function parseIp(addr: string): number[] {
  return (addr.match(IPV4_REGEXP) || []).slice(1, 5).map(Number);
}

/**
 * Calculates network IP.
 *
 * @param addr Add spec.
 * @param subnetMask Subnet mask.
 * @returns
 */
export function calculateNetworkIp(addr: string, subnetMask: string): string {
  return SUBNET_MASKS.has(subnetMask)
    ? zip_short(parseIp(addr), parseIp(subnetMask))
        .map(([a, b]) => a & b)
        .join('.')
    : '';
}

/**
 * Calculates the broadcast IP address.
 *
 * @param addr Address spec.
 * @param subnetMask  Subnet mask.
 * @returns the broadcast IP.
 */
export function calculateBroadcastIp(addr: string, subnetMask: string): string {
  return SUBNET_MASKS.has(subnetMask)
    ? zip_short(parseIp(addr), parseIp(subnetMask))
        .map(([a, b]) => toUnsigned(a | ~b, 8))
        .join('.')
    : '';
}

/**
 * @param addr The address spec.
 * @returns The value of the address spec.
 */
export function valueOfIp(addr: string): number {
  const ip = parseIp(addr)
    .map(o => o.toString(16).padStart(2, '0'))
    .join('');
  return Number.parseInt(`0x${ip}`, 16);
}

/**
 * @param num Number
 * @param width Width
 * @returns  Unsigned representation.
 */
export function toUnsigned(num: number, width: number): number {
  return num & ((1 << width) - 1);
}
